import gsap from "gsap";

import { prefersReducedMotion, isNavMobile, inner, varGap } from "../utils/variables";
import { toggleScroll } from "../utils/functions";
import { scroll } from "./scroll";

const frameFadeDuration = 1;
const imageFadeDuration = 0.5;
const holdDuration = 0.7;
const insetScaleDuration = 0.7;
const outroDuration = 0.6;
const heroRevealDuration = 0.5;
const titleRestScale = 0.94;
const imageParallaxScale = 1.5;
const imageRestScale = 1.03;

let resolveTitleRevealed: () => void;
export const titleRevealed = new Promise<void>(resolve => {
    resolveTitleRevealed = resolve;
});

let resolveNavRevealed: () => void;
export const navRevealed = new Promise<void>(resolve => {
    resolveNavRevealed = resolve;
});

export function runLoader() {
    const loader = document.querySelector<HTMLElement>(".loader");
    if (!loader) {
        resolveTitleRevealed();
        resolveNavRevealed();
        return Promise.resolve();
    }

    const finish = (heroBg?: HTMLElement | null) => {
        if (heroBg) gsap.set(heroBg, { clearProps: "top,left,width,height,borderRadius" });
        localStorage.setItem("hasSeenLoader", "true");
        document.documentElement.classList.remove("show-loader");
        loader.remove();
        document.documentElement.style.overflow = "";
        toggleScroll("enable");
        if ("scrollRestoration" in history) history.scrollRestoration = "auto";
    };

    if (!document.documentElement.classList.contains("show-loader")) {
        loader.remove();
        resolveTitleRevealed();
        resolveNavRevealed();
        return Promise.resolve();
    }

    if (prefersReducedMotion || isNavMobile) {
        finish();
        resolveTitleRevealed();
        resolveNavRevealed();
        return Promise.resolve();
    }

    const frame = loader.querySelector<HTMLElement>(".loader__frame");
    const [firstImage, secondImage, finalImage] = loader.querySelectorAll<HTMLElement>(".loader__img");
    const heroBg = document.querySelector<HTMLElement>(".hero__bg");
    const heroTitle = document.querySelector<HTMLElement>(".hero .display");

    if (!frame || !firstImage || !secondImage || !finalImage || !heroBg) {
        finish();
        resolveTitleRevealed();
        resolveNavRevealed();
        return Promise.resolve();
    }

    window.scrollTo(0, 0);
    scroll.scrollTo(0, { immediate: true });
    document.documentElement.style.overflow = "hidden";
    toggleScroll("disable");

    const insetWidth = inner.w - varGap * 2;
    const insetHeight = inner.h - varGap * 2;
    const insetRadius = getComputedStyle(frame).borderRadius;

    gsap.set(heroBg, {
        top: varGap,
        left: varGap,
        width: insetWidth,
        height: insetHeight,
        borderRadius: insetRadius,
    });

    if (heroTitle) gsap.set(heroTitle, { scale: titleRestScale });

    return new Promise<void>(resolve => {
        const tl = gsap.timeline({
            onComplete: () => finish(heroBg),
        });

        tl.to(frame, { autoAlpha: 1, duration: frameFadeDuration })
            .to(firstImage, { autoAlpha: 1, duration: imageFadeDuration })
            .to({}, { duration: holdDuration })
            .set(firstImage, { autoAlpha: 0 })
            .set(secondImage, { autoAlpha: 1 })
            .to({}, { duration: holdDuration })
            .set(secondImage, { autoAlpha: 0 })
            .set(finalImage, { autoAlpha: 1 })
            .to({}, { duration: holdDuration })
            .to(frame, {
                width: insetWidth,
                height: insetHeight,
                duration: insetScaleDuration,
                onStart: () => {
                    setTimeout(() => {
                        resolveTitleRevealed();
                    }, 200);
                },
            })
            .fromTo(finalImage, { scale: imageParallaxScale }, { scale: imageRestScale, duration: insetScaleDuration }, "<")
            .to(
                loader,
                {
                    autoAlpha: 0,
                    duration: outroDuration,
                    onStart: () => resolve(),
                },
                "-=.2"
            )
            .to(
                heroBg,
                {
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    borderRadius: 0,
                    duration: heroRevealDuration,
                    onStart: () => resolveNavRevealed(),
                },
                "+=.7"
            );

        if (heroTitle) tl.to(heroTitle, { scale: 1, duration: heroRevealDuration }, "<");
    });
}
